<template>
  <div class="card-list-data">
    <div class="row row-cols-2 row-cols-sm-3 row-cols-lg-4 g-3" v-if="!IS_LOADER">
      <!-- 1. No Preference Card -->
      <div class="col">
        <div class="iq-widget h-100">
          <input type="radio" id="no-preference" v-model="employee_id" value="no_preference" name="radio"
            class="btn-check" @change="onChange" />
          <label for="no-preference" class="d-block h-100">
            <div class="card staff-grid-card border-0 shadow-sm h-100 mb-0">
              <div class="card-body text-center d-flex flex-column align-items-center justify-content-center p-3">
                <div class="staff-avatar-placeholder-box mb-3">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M16 21V19C16 16.7909 14.2091 15 12 15H5C2.79086 15 1 16.7909 1 19V21M12 7C12 9.20914 10.2091 11 8 11C5.79086 11 4 9.20914 4 7C4 4.79086 5.79086 3 8 3C10.2091 3 12 4.79086 12 7Z" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M17 11C19.2091 11 21 9.20914 21 7C21 4.79086 19.2091 3 17 3C14.7909 3 13 4.79086 13 7C13 9.20914 14.7909 11 17 11Z" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M23 21V19C23 16.7909 21.2091 15 19 15" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <h6 class="staff-grid-name mb-0 fw-bold">{{ $t('quick_booking.no_preference') || 'No Preference' }}</h6>
                <div class="selection-indicator-grid"></div>
              </div>
            </div>
          </label>
        </div>
      </div>

      <!-- 2. Staff Grid Items -->
      <div class="col" v-for="(item, index) in visibleEmployees" :key="`staff-${item.id}`">
        <div class="iq-widget h-100">
          <input type="radio" :id="`staff-${item.id}`" v-model="employee_id" :value="String(item.id)"
            name="radio" class="btn-check" @change="onChange" />
          <label :for="`staff-${item.id}`" class="d-block h-100">
            <div class="card staff-grid-card border-0 shadow-sm h-100 mb-0">
              <div class="card-body text-center d-flex flex-column align-items-center justify-content-center p-3">
                <div class="staff-avatar-grid-box mb-3">
                  <!-- State-aware Image Fallback -->
                  <img :src="item.profile_image" class="staff-avatar-img" :alt="displayName(item)" 
                    @error="handleImageError(item.id)" v-if="item.profile_image && !erroredImages[item.id]"/>
                  <div class="staff-avatar-placeholder-box" v-else>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" stroke-linecap="round" stroke-linejoin="round"/>
                     </svg>
                  </div>
                </div>
                <h6 class="staff-grid-name mb-0 fw-bold text-truncate w-100">{{ displayName(item) }}</h6>
                <div class="selection-indicator-grid"></div>
              </div>
            </div>
          </label>
        </div>
      </div>
    </div>

    <!-- Loader Skeleton -->
    <div v-else class="row row-cols-2 row-cols-sm-3 row-cols-lg-4 g-3">
      <div class="col" v-for="index in 5" :key="index">
        <div class="card border-0 shadow-none overflow-hidden h-100 mb-0">
          <div class="card-body text-center d-flex flex-column align-items-center justify-content-center p-3">
            <div class="skeleton-avatar-grid mb-3"></div>
            <div class="skeleton-line-grid w-75"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- No Employee Found -->
    <div class="h-100 w-75 d-flex align-items-center justify-content-center mx-auto text-center" v-if="employeeList.length == 0">
      {{ $t('quick_booking.no_qualified_staff_available') || 'No qualified staff members available for the selected service at this branch.' }}
    </div>
  </div>

  <!-- Footer Buttons -->
  <div class="card-footer">
    <button type="button" class="btn btn-secondary iq-text-uppercase" v-if="wizardPrev"
      @click="prevTabChange(wizardPrev)">
      {{ $t('quick_booking.lbl_back') }}
    </button>
    <button type="button" v-if="employeeList.length > 0 && wizardNext" class="btn btn-primary iq-text-uppercase"
      @click="nextTabChange(wizardNext)">
      {{ $t('quick_booking.lbl_next') }}
    </button>
  </div>
</template>
<script setup>
import { ref, computed, watch, onMounted } from 'vue' // <-- added onMounted
import { useRequest } from '@/helpers/hooks/useCrudOpration'
import { STAFF_LIST } from '@/vue/constants/quick_booking'
import { useQuickBooking } from '../../store/quick-booking'

const props = defineProps({
  wizardNext: { default: '', type: [String, Number] },
  wizardPrev: { default: '', type: [String, Number] }
})
const emit = defineEmits(['tab-change', 'onReset'])

const { listingRequest } = useRequest()
const store = useQuickBooking()

const NO_PREFERENCE_VALUE = 'no_preference'

const employeeList = ref([])
const employee_id = ref(NO_PREFERENCE_VALUE)
const IS_LOADER = ref(true)
const erroredImages = ref({})

// Public website (030): names without calendar codes ("Гери West" → "Гери"), calendar
// columns that are not people left out, and ?staff=… pre-ticked once it qualifies.
const pub = (typeof window !== 'undefined' && window.QB_STAFF) || {}
const pre = (typeof window !== 'undefined' && window.QB_PRESELECT) || {}
const hiddenIds = (pub.hidden || []).map(Number)
const displayName = (item) => (pub.names && pub.names[item.id]) || item.full_name
const visibleEmployees = computed(() => employeeList.value.filter((item) => !hiddenIds.includes(Number(item.id))))
let preStaffApplied = false

const handleImageError = (id) => {
  erroredImages.value[id] = true
}

const serviceIdsKey = computed(() =>
  (store.booking.services || [])
    .map((service) => service.service_id)
    .filter((id) => id !== null && id !== undefined)
    .join(',')
)

const hasExistingSelection = () => {
  return ![null, undefined, '', NO_PREFERENCE_VALUE].includes(store.booking.employee_id)
}

const applySelection = ({ emitEvent = false } = {}) => {
  const rawValue = employee_id.value
  const value = rawValue === NO_PREFERENCE_VALUE ? null : Number(rawValue)

  store.updateBookingEmployeeValues(value)
  store.updateBookingValues({ key: 'employee_id', value })

  const tokenNotSet = store.booking.employee_selection_token === null || store.booking.employee_selection_token === undefined

  if (emitEvent || tokenNotSet) {
    store.updateBookingValues({ key: 'employee_selection_token', value: Date.now() })
  }

  if (emitEvent) {
    emit('tab-change', props.wizardNext)
  }
}

const ensureDefaultSelection = () => {
  employee_id.value = NO_PREFERENCE_VALUE
  applySelection()
}

const getStaffs = () => {
  console.log('getStaffs called (optimized) ✅')
  IS_LOADER.value = true
  
  // Clear existing selection and list to ensure fresh state
  employeeList.value = []
  
  const data = {
    branch_id: store.booking.branch_id,
    service_id: serviceIdsKey.value
  }

  if (data.branch_id && data.service_id) {
    listingRequest({ url: STAFF_LIST, data: data })
      .then((res) => {
        IS_LOADER.value = false
        if (res?.data && res.data.length > 0) {
          employeeList.value = res.data
        } else {
          employeeList.value = []
        }

        if (!preStaffApplied && pre.employee_id && visibleEmployees.value.some(emp => String(emp.id) === String(pre.employee_id))) {
          preStaffApplied = true
          employee_id.value = String(pre.employee_id)
          applySelection()
          return
        }

        // Handle existing selection persistence if valid
        if (hasExistingSelection()) {
          const stillQualified = employeeList.value.some(emp => String(emp.id) === String(store.booking.employee_id))
          if (stillQualified) {
            employee_id.value = String(store.booking.employee_id)
            applySelection()
          } else {
            ensureDefaultSelection()
          }
        } else {
          ensureDefaultSelection()
        }
      })
      .catch((err) => {
        console.error('Staff API Failed ❌', err)
        IS_LOADER.value = false
        employeeList.value = []
        ensureDefaultSelection()
      })
  } else {
    IS_LOADER.value = false
    employeeList.value = []
    ensureDefaultSelection()
  }
}

watch(
  serviceIdsKey,
  () => {
    getStaffs()
  }
)

watch(
  () => store.bookingResponse,
  () => {
    resetData()
  },
  { deep: true }
)
watch(
  () => store.booking.branch_id,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      resetData()
    }
    if (newVal) {
      getStaffs()
    }
  }
)

onMounted(() => {
  getStaffs() // <-- call getStaffs manually on first load
})

const onChange = () => {
  applySelection({ emitEvent: true })
}

const nextTabChange = (val) => emit('tab-change', val)

const prevTabChange = (val) => {
  resetData()
  emit('tab-change', val)
}

const resetData = () => {
  employee_id.value = NO_PREFERENCE_VALUE
  store.updateBookingEmployeeValues(null)
  store.updateBookingValues({ key: 'employee_id', value: null })
  store.updateBookingValues({ key: 'employee_selection_token', value: null })
}
</script>

<style scoped>
.staff-grid-card {
  background-color: #ffffff;
  border: 1.5px solid #edeff2 !important;
  border-radius: 16px !important;
  cursor: pointer;
  transition: border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 200px;
  box-shadow: 0 1px 2px rgba(16, 18, 43, 0.04) !important;
}

.staff-grid-card:hover {
  border-color: rgba(var(--bs-primary-rgb), 0.45) !important;
  box-shadow: 0 12px 26px -14px rgba(16, 18, 43, 0.28) !important;
  transform: translateY(-3px);
}

/* The face is how a returning client recognises their stylist, so the avatar
   scales with the column instead of sitting at a fixed thumbnail size. */
.staff-avatar-grid-box, .staff-avatar-placeholder-box {
  width: clamp(96px, 26vw, 132px);
  height: clamp(96px, 26vw, 132px);
  border-radius: 50%;
  overflow: hidden;
  background: #f6f7fa;
  border: 3px solid #ffffff;
  box-shadow: 0 6px 18px -10px rgba(16, 18, 43, 0.4);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.staff-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.staff-avatar-placeholder-box {
  background: rgba(var(--bs-primary-rgb), 0.05);
  color: var(--bs-primary);
}

.staff-grid-name {
  font-size: 15px;
  color: #16182b;
  line-height: 1.3;
  letter-spacing: -0.005em;
}

.selection-indicator-grid {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 20px;
  height: 20px;
  border: 2px solid #edeff2;
  border-radius: 50%;
  transition: all 0.2s ease-in-out;
}

.iq-widget .btn-check:checked + label .staff-grid-card {
  border-color: var(--bs-primary) !important;
  background-color: rgba(var(--bs-primary-rgb), 0.05) !important;
  box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0.14) !important;
}

.iq-widget .btn-check:checked + label .selection-indicator-grid {
  border-color: var(--bs-primary);
  background-color: var(--bs-primary);
}

.iq-widget .btn-check:checked + label .selection-indicator-grid::after {
  content: '';
  position: absolute;
  top: 52%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(45deg);
  width: 5px;
  height: 9px;
  border: solid white;
  border-width: 0 2px 2px 0;
}

/* Skeleton Grid */
.skeleton-avatar-grid {
  width: clamp(96px, 26vw, 132px);
  height: clamp(96px, 26vw, 132px);
  border-radius: 50%;
  background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
  background-size: 200% 100%;
  animation: grid-shine 1.5s linear infinite;
}

.skeleton-line-grid {
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(110deg, #ececec 8%, #f5f5f5 18%, #ececec 33%);
  background-size: 200% 100%;
  animation: grid-shine 1.5s linear infinite;
}

@keyframes grid-shine {
  to { background-position-x: -200%; }
}
</style>
